import { parseAsStringLiteral, useQueryState } from "nuqs"

import { FilterChip } from "@/components/ui/filter-chip"
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from "@/components/ui/select"
import { tStats } from "@/features/staff/stats/format"
import {
  DEFAULT_STATS_RANGE,
  STATS_RANGES,
  type StatsRangeOption,
  type StatsRangeProps,
} from "@/features/staff/stats/types"

/**
 * The range control, and the sentence that says what the window actually is.
 *
 * **The chip names the WINDOW; the bucket is derived from it and printed.**
 * They are two things, and collapsing them is how a trend screen lies: thirty
 * days bucketed by month is one point, twelve months bucketed by day is three
 * hundred and sixty five. It is also the only reason "6 meses" and "12 meses"
 * are not the same control at two lengths — six months stays on the week.
 *
 * **The control is responsive, and that is a real decision rather than a
 * breakpoint reflex.** Five chips at the 44px target every interactive control
 * owes a phone wrap to three rows and push the first figure below the fold. So
 * below `md` they collapse into the kit's Select, whose trigger is the same
 * 44px, and each option carries its bucket beside it — the moment somebody is
 * choosing is exactly where the ladder is worth teaching.
 *
 * The range lives in the URL. `.claude/rules/react.md` §3 makes URL state the
 * source of truth for filters, and it means something concrete here: an owner
 * who wants to send "mira el año" sends a link. The write goes through
 * `NuqsInertiaAdapter`, so it is a real Inertia visit and every figure comes
 * back from the server.
 */
export function StatsRangeControl({
  range,
  options,
}: {
  range: StatsRangeProps
  options: StatsRangeOption[]
}) {
  const [value, setValue] = useQueryState(
    "range",
    parseAsStringLiteral(STATS_RANGES)
      .withDefault(DEFAULT_STATS_RANGE)
      // nuqs drops a param equal to its default. Not here: this URL gets pasted
      // into a message and bookmarked, and a link reading `/manage/stats` means
      // "whatever the default is on the day you open it".
      .withOptions({ clearOnDefault: false })
  )

  const active = options.find((option) => option.value === value)

  return (
    <div className="space-y-2" data-testid="stats-range-control">
      {/* Phone: one control, one row. */}
      <div className="space-y-1.5 md:hidden">
        <label
          htmlFor="stats-range-select"
          className="block text-xs text-muted-foreground"
        >
          {tStats("bucket_field")}
        </label>
        <Select value={value} onValueChange={(next) => setValue(next as typeof value)}>
          <SelectTrigger
            id="stats-range-select"
            className="w-full"
            data-testid="stats-range-trigger"
          >
            <SelectValue />
          </SelectTrigger>
          <SelectContent>
            {options.map((option) => (
              <SelectItem
                key={option.value}
                value={option.value}
                data-testid={`range-option-${option.value}`}
              >
                <span className="flex-1">{option.label}</span>
                <span className="text-xs text-muted-foreground">{option.bucket}</span>
              </SelectItem>
            ))}
          </SelectContent>
        </Select>
      </div>

      {/* Desktop: five chips fit on one row, and seeing the whole ladder at once
          is worth more than the pixels it costs. */}
      <div className="hidden flex-wrap gap-2 md:flex">
        {options.map((option) => (
          <FilterChip
            key={option.value}
            active={value === option.value}
            // The kit's filter height is `sm`. This is the primary control of
            // the screen, so it takes the standard control height — the same
            // override, for the same reason, that the period bar makes.
            className="md:h-9"
            data-testid={`range-chip-${option.value}`}
            onClick={() => setValue(option.value)}
          >
            {option.label}
          </FilterChip>
        ))}
      </div>

      <p className="text-xs tabular-nums text-muted-foreground" data-testid="stats-range-label">
        {tStats("scope", {
          label: range.label,
          buckets: tStats(`closed_buckets.${range.granularity}`, { count: range.closedBucketCount }),
          bucket: active?.bucket ?? "",
        })}
      </p>

      <p className="text-xs text-muted-foreground">{tStats("notes.open_bucket")}</p>

      {range.granularity === "day" ? (
        <p className="text-xs text-muted-foreground">{tStats("notes.same_weekday")}</p>
      ) : null}
    </div>
  )
}
